import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  MultiSelectModal,
  SectionCard,
  Skeleton,
  StatusBadge,
  RowActions,
  type RowActionItem,
} from '@/components/ui';
import { ImageOff, Plus } from 'lucide-react';
import { fetchProductOptions, resolveProductOption } from '@/features/catalog/components/productSelectLoaders';
import { SortableList } from '../../components/SortableList';
import { friendlyMessage } from '@/lib/errors';
import { toast } from '@/components/ui';
import {
  isAutoSection,
  useHomeSectionDisplayModeMutation,
  useHomeSectionProductMutations,
  useHomeSectionProductsQuery,
  useHomeSectionReorderMutation,
  useHomeSectionsQuery,
} from '../hooks/useHomeSectionsQuery';
import type { HomeSection, HomeSectionDisplayMode, HomeSectionProduct } from '../types/homeSection';
import { sectionProductNotes } from '../lib/sectionProductVisibility';

/**
 * QA-570 — 진열 방법(수동/자동). 자동이면 쇼핑몰 메인은 아래 매핑 대신 공개 상품 최신 등록순으로 보인다
 * (EVER_MD 는 고도몰 자동 진열이라 BE V145 로 자동). 매핑은 지우지 않아 수동으로 되돌리면 그대로 쓴다.
 */
const DisplayModeControl = ({ section }: { section: HomeSection }) => {
  const change = useHomeSectionDisplayModeMutation();
  const mode: HomeSectionDisplayMode = isAutoSection(section) ? 'AUTO' : 'MANUAL';
  const pick = (next: HomeSectionDisplayMode) => {
    if (next === mode || change.isPending) return;
    change.mutate(
      { sectionId: section.id, displayMode: next },
      {
        onSuccess: () => toast.success(next === 'AUTO' ? '자동 진열로 바꿨습니다.' : '수동 진열로 바꿨습니다.'),
        onError: (e) => toast.error(friendlyMessage(e, '진열 방법을 바꾸지 못했습니다.')),
      },
    );
  };
  const option = (value: HomeSectionDisplayMode, label: string) => (
    <label className="inline-flex items-center gap-1.5 text-sm" style={{ color: 'var(--content-default)' }}>
      <input
        type="radio"
        name={`home-section-display-mode-${section.id}`}
        checked={mode === value}
        disabled={change.isPending}
        onChange={() => pick(value)}
        data-testid={`home-section-display-mode-${value}`}
      />
      {label}
    </label>
  );
  return (
    <div className="mb-3 flex flex-col gap-1.5" data-testid="home-section-display-mode">
      <div className="flex flex-wrap items-center gap-4">
        <span className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>진열 방법</span>
        {option('MANUAL', '수동 진열(아래 상품만)')}
        {option('AUTO', '자동 진열(최신 등록순)')}
      </div>
      {mode === 'AUTO' ? (
        <p
          className="rounded-md px-2.5 py-1.5 text-xs"
          style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
          role="note"
          data-testid="home-section-auto-note"
        >
          자동 진열 중입니다. 쇼핑몰 메인에는 아래 상품이 아니라 공개 상품이 최신 등록순으로 나옵니다. 아래 상품을 보이려면 수동 진열로 바꿔 주세요.
        </p>
      ) : null}
    </div>
  );
};

const SectionListItem = ({
  section,
  selected,
  onSelect,
  dragHandle,
}: {
  section: HomeSection;
  selected: boolean;
  onSelect: () => void;
  dragHandle: React.ReactNode;
}) => {
  const { t } = useTranslation('common');
  return (
    <div
      className="flex items-center gap-1.5 rounded-md px-2 py-1.5"
      style={{
        background: selected ? 'var(--surface-1)' : 'transparent',
        border: '1px solid',
        borderColor: selected ? 'var(--accent-default)' : 'var(--divider-strong)',
      }}
    >
      {dragHandle}
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left text-sm"
        style={{ color: 'var(--content-strong)' }}
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-medium">{section.label}</span>
          <span className="truncate text-xs" style={{ color: 'var(--content-muted)' }}>
            {section.sectionKey}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {isAutoSection(section) ? <StatusBadge variant="warning" size="sm" label="자동" /> : null}
          {section.isActive ? (
            <StatusBadge variant="success" size="sm" label={t('cms.active')} />
          ) : (
            <StatusBadge variant="neutral" size="sm" label={t('cms.inactive')} />
          )}
        </span>
      </button>
    </div>
  );
};

const ProductMappingTable = ({ section }: { section: HomeSection }) => {
  const { t } = useTranslation('common');
  const query = useHomeSectionProductsQuery(section.id);
  const { add, reorder, remove } = useHomeSectionProductMutations();
  const [modalOpen, setModalOpen] = useState(false);

  // 서버 순서를 로컬에 미러 — 드래그 즉시 반영(낙관적), 재조회 시 동기화.
  const [rows, setRows] = useState<HomeSectionProduct[]>([]);
  useEffect(() => {
    setRows(query.data ?? []);
  }, [query.data]);

  const currentIds = useMemo(() => rows.map((p) => p.productId), [rows]);

  const handleReorder = (next: HomeSectionProduct[]): void => {
    setRows(next); // 낙관적 표시
    reorder.mutate(
      { sectionId: section.id, productIds: next.map((p) => p.productId) },
      { onError: () => setRows(query.data ?? []) },
    );
  };

  return (
    <SectionCard
      title={section.label}
      aside={
        <Button
          size="sm"
          disabled={add.isPending}
          onClick={() => setModalOpen(true)}
          data-testid="home-section-add-products"
        >
          <Plus size={15} aria-hidden="true" />
          {t('cms.mainPage.addProducts')}
        </Button>
      }
    >
      <DisplayModeControl section={section} />
      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[52px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('cms.mainPage.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('cms.retry')}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg py-10 text-center text-sm"
          style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
        >
          {t('cms.mainPage.productsEmpty')}
        </div>
      ) : (
        <>
          <p className="mb-2 text-xs" style={{ color: 'var(--content-muted)' }}>
            {t('cms.mainPage.dragHint', { defaultValue: '행 왼쪽 손잡이를 드래그하여 전시 순서를 변경합니다.' })}
          </p>
          {rows.some((row) => sectionProductNotes(row).length > 0) ? (
            <p className="mb-2 text-xs" style={{ color: 'var(--content-muted)' }} data-testid="home-section-visibility-hint">
              표시가 붙은 상품은 쇼핑몰 메인에 나오지 않거나, 적힌 몰에서만 나옵니다.
            </p>
          ) : null}
          <SortableList
            items={rows}
            getId={(row) => row.productId}
            onReorder={handleReorder}
            disabled={reorder.isPending}
            handleLabel={t('cms.mainPage.dragHandleLabel', { defaultValue: '상품 순서 이동 핸들' })}
            className="flex flex-col gap-1.5"
          >
            {(row, _index, dragHandle) => {
              const actions: RowActionItem[] = [
                {
                  key: 'remove',
                  label: t('cms.mainPage.action.remove'),
                  variant: 'danger',
                  disabled: remove.isPending,
                  onClick: () => remove.mutate({ sectionId: section.id, productId: row.productId }),
                },
              ];
              return (
                <div
                  className="flex items-center gap-2 rounded-md px-2 py-2"
                  style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
                >
                  {dragHandle}
                  <div
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded"
                    style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
                  >
                    {row.thumbnailUrl ? (
                      <img src={row.thumbnailUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <ImageOff size={14} style={{ color: 'var(--content-soft)' }} aria-hidden="true" />
                    )}
                  </div>
                  <span
                    className="min-w-0 flex-1 truncate text-sm font-medium"
                    style={{ color: 'var(--content-strong)' }}
                    title={row.name}
                  >
                    {row.name}
                  </span>
                  {sectionProductNotes(row).map((note) => (
                    <StatusBadge
                      key={note.key}
                      variant={note.kind === 'hidden' ? 'danger' : 'warning'}
                      size="sm"
                      label={note.label}
                      data-testid={`home-section-product-note-${row.productId}-${note.key}`}
                    />
                  ))}
                  <RowActions triggerLabel={t('cms.mainPage.rowActionsLabel')} actions={actions} />
                </div>
              );
            }}
          </SortableList>
        </>
      )}

      <MultiSelectModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        loadOptions={fetchProductOptions}
        resolveOption={resolveProductOption}
        initialValue={currentIds}
        onApply={(values) => {
          const next = values.filter((id) => !currentIds.includes(id));
          if (next.length > 0) add.mutate({ sectionId: section.id, productIds: next });
        }}
        title={t('cms.mainPage.modalTitle')}
        description={t('cms.mainPage.modalDescription')}
        searchPlaceholder={t('cms.mainPage.modalSearchPlaceholder')}
        applyLabel={t('cms.mainPage.modalApply')}
      />
    </SectionCard>
  );
};

export const SectionProductMappingPanel = () => {
  const { t } = useTranslation('common');
  const sectionsQuery = useHomeSectionsQuery();
  const { reorder: reorderSections } = useHomeSectionReorderMutation();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // 서버 순서를 로컬에 미러 — 드래그 즉시 반영(낙관적), 재조회 시 동기화.
  const [sections, setSections] = useState<HomeSection[]>([]);
  useEffect(() => {
    setSections(sectionsQuery.data ?? []);
  }, [sectionsQuery.data]);

  // 첫 섹션 자동 선택(목록 로드 시).
  useEffect(() => {
    if (selectedId == null && sections.length > 0) {
      setSelectedId(sections[0]?.id ?? null);
    }
  }, [sections, selectedId]);

  const handleReorderSections = (next: HomeSection[]): void => {
    setSections(next); // 낙관적 표시
    reorderSections.mutate(
      next.map((s) => s.id),
      { onError: () => setSections(sectionsQuery.data ?? []) },
    );
  };

  const selectedSection = sections.find((s) => s.id === selectedId) ?? null;

  if (sectionsQuery.isPending) {
    return (
      <div role="status" aria-live="polite" className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[44px] w-full" />
        ))}
      </div>
    );
  }

  if (sectionsQuery.isError) {
    return (
      <div
        role="alert"
        aria-live="polite"
        className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
        style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
      >
        <p>{t('cms.mainPage.sectionsLoadFailed')}</p>
        <Button variant="outline" size="sm" onClick={() => sectionsQuery.refetch()}>
          {t('cms.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
      <div className="md:col-span-1">
        <SectionCard title={t('cms.mainPage.sectionsTitle')}>
          <p className="mb-2 text-xs" style={{ color: 'var(--content-muted)' }}>
            {t('cms.mainPage.sectionDragHint', { defaultValue: '손잡이를 드래그하여 섹션 노출 순서를 변경합니다.' })}
          </p>
          <SortableList
            items={sections}
            getId={(s) => s.id}
            onReorder={handleReorderSections}
            disabled={reorderSections.isPending}
            handleLabel={t('cms.mainPage.sectionDragHandleLabel', { defaultValue: '섹션 순서 이동 핸들' })}
            className="flex flex-col gap-2"
          >
            {(section, _index, dragHandle) => (
              <SectionListItem
                section={section}
                selected={section.id === selectedId}
                onSelect={() => setSelectedId(section.id)}
                dragHandle={dragHandle}
              />
            )}
          </SortableList>
        </SectionCard>
      </div>

      <div className="md:col-span-2">
        {selectedSection ? (
          <ProductMappingTable section={selectedSection} />
        ) : (
          <SectionCard title={t('cms.mainPage.sectionsTitle')}>
            <p className="py-8 text-center text-sm" style={{ color: 'var(--content-muted)' }}>
              {t('cms.mainPage.selectSection')}
            </p>
          </SectionCard>
        )}
      </div>
    </div>
  );
};
